import { useState } from 'react'

import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useTourPlanCheckpoints } from '../hooks/useTourPlanCheckpoints'
import { useSelector } from 'react-redux'

import { CheckpointIProps } from '@/features/floor-plan'
import { selectMe } from '@/redux/me/meSlice'
import { selectEditMode } from '@/redux/ui/uiSlice'
import { mixpanel } from '@/utils/analytics'

import { EditCheckPoint } from '../../checkpoints-panel/components/EditCheckPoint'
import { EditTourPlan } from './EditTourPlan'
import { TourPlanCheckPointsList } from './TourPlanCheckPointsList'

enum TourPlanState {
  CheckpointsList,
  EditCheckpoint,
  EditTourPlan,
}

export interface CheckPointsPanelIProps {
  facilityId: string
  tourPlanId: string
  currentFloorId: string
  onClose: () => void
  onUpdateTourPlan: (id?: string) => void
  onDeleteTourPlan: () => void
  onDeviceItemHoverIn: (deviceId: string) => void
}

export const TourPlanCheckPoints = ({
  facilityId,
  tourPlanId,
  currentFloorId,
  onClose,
  onUpdateTourPlan,
  onDeleteTourPlan,
  onDeviceItemHoverIn,
}: CheckPointsPanelIProps) => {
  const me = useSelector(selectMe)
  const isEditMode = useSelector(selectEditMode)
  const { id, name, checkpoints, isLoading } =
    useTourPlanCheckpoints(tourPlanId)
  const [editCheckPoint, setEditCheckPoint] = useState<CheckpointIProps>(null)
  const [viewState, setViewState] = useState<TourPlanState>(
    TourPlanState.CheckpointsList
  )
  const { setHighlightedDeviceIds } = useFloorPlan()

  const handleOnClick = (checkpoint: CheckpointIProps) => {
    setEditCheckPoint(checkpoint)
    setViewState(TourPlanState.EditCheckpoint)
    mixpanel.track('Button Press: Tour Plan Details - Checkpoint', {
      facility_id: facilityId,
      tour_plan_id: tourPlanId,
      checkpoint_id: checkpoint?.id,
      role: me.roleName,
    })
    isEditMode &&
      mixpanel.track('Button Press: Tour Plan Details - Checkpoint Edit', {
        facility_id: facilityId,
        tour_plan_id: tourPlanId,
        checkpoint_id: checkpoint?.id,
        role: me.roleName,
      })
  }

  const handleEditClick = () => {
    setViewState(TourPlanState.EditTourPlan)
    mixpanel.track('Button Press: Tour Plan Details - Edit Tour', {
      facility_id: facilityId,
      tour_plan_id: tourPlanId,
      role: me.roleName,
    })
  }

  const handleOnClose = () => {
    isEditMode &&
      mixpanel.track(
        'Button Press: Tour Plan Details - Checkpoint Edit Cancel',
        {
          facility_id: facilityId,
          tour_plan_id: tourPlanId,
          checkpoint_id: editCheckPoint?.id,
          role: me.roleName,
        }
      )
    setEditCheckPoint(null)
    setHighlightedDeviceIds(null)
    setViewState(TourPlanState.CheckpointsList)
  }

  const handleOnDone = () => {
    setHighlightedDeviceIds(null)
    onClose()
    mixpanel.track('Button Press: Tour Plan Details - Checkpoint Edit Save', {
      facility_id: editCheckPoint?.facility?.id,
      tour_plan_id: tourPlanId,
      checkpoint_id: editCheckPoint?.id,
      role: me.roleName,
    })
  }

  const backToTourCheckpoints = (id?: string) => {
    setViewState(TourPlanState.CheckpointsList)
    if (id) {
      onUpdateTourPlan(id)
    }
  }

  return (
    <>
      {viewState === TourPlanState.CheckpointsList && (
        <TourPlanCheckPointsList
          checkpoints={checkpoints}
          isLoading={isLoading}
          name={name}
          onCheckPointClick={handleOnClick}
          onClose={onClose}
          onDeviceItemHoverIn={onDeviceItemHoverIn}
          onEditClick={handleEditClick}
        />
      )}
      {viewState === TourPlanState.EditCheckpoint && (
        <EditCheckPoint
          checkpoint={editCheckPoint}
          onClose={handleOnClose}
          onDone={handleOnDone}
        />
      )}
      {viewState === TourPlanState.EditTourPlan && (
        <EditTourPlan
          checkpoints={checkpoints}
          currentFloorId={currentFloorId}
          facilityId={facilityId}
          id={id}
          isLoading={isLoading}
          name={name}
          onClose={backToTourCheckpoints}
          onDeleteTourPlan={onDeleteTourPlan}
          onUpdateTourPlan={backToTourCheckpoints}
        />
      )}
    </>
  )
}
